import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography,
} from "@mui/material";
import { ArrowForward, Verified } from "@mui/icons-material";
import { getData, postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import { ProjectDetail, ReferenceDraft } from "../../types/projectTypes";
import { formatMoney, REFERENCE_CONVERSION_ENABLED } from "./projectUtils";

type ProjectReferenceTabProps = {
    detail: ProjectDetail;
    setLoading: Function;
    refresh: () => Promise<void>;
};

type WizardFields = {
    ProjectTitle: string;
    ProjectTitle_short: string;
    StartDate: string;
    CompletionDate: string;
    Location: string;
    FieldOfService: string;
    ProjectDescription_short: string;
    ServicesProvided: string;
};

// "Close & create reference": a successfully closed project becomes a reference. The wizard
// shows what is taken over from the project and lets the user complete the reference texts.
function ProjectReferenceTab({ detail, setLoading, refresh }: ProjectReferenceTabProps) {
    const navigate = useNavigate();
    const notify = useNotification();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<ReferenceDraft | null>(null);
    const [fields, setFields] = useState<WizardFields | null>(null);
    const [busy, setBusy] = useState(false);
    const { project, reference } = detail;

    const openWizard = async () => {
        setOpen(true);
        setDraft(null);
        try {
            const result = await getData(`projects/${project.ID}/reference-draft`) as ReferenceDraft;
            setDraft(result);
            setFields({
                ProjectTitle: result.ProjectTitle,
                ProjectTitle_short: result.ProjectTitle_short,
                StartDate: result.StartDate || '',
                CompletionDate: result.CompletionDate || '',
                Location: '',
                FieldOfService: '',
                ProjectDescription_short: '',
                ServicesProvided: '',
            });
        } catch (error) {
            notify(`Could not prepare the reference: ${(error as Error).message}`, 'error');
            setOpen(false);
        }
    };

    const convert = async () => {
        if (!fields) return;
        if (!fields.ProjectTitle.trim()) {
            notify('The reference needs a title.', 'warning');
            return;
        }
        setBusy(true);
        setLoading(true);
        try {
            const body = Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== ''));
            const result = await postData(`projects/${project.ID}/convert-to-reference`, body);
            notify(result.created
                ? `Reference ${result.projectNo} created - ${result.expertsCopied} expert(s) and ${result.partnersCopied} partner(s) copied.`
                : `Linked to the existing reference ${result.projectNo} - ${result.expertsCopied} expert(s) and ${result.partnersCopied} partner(s) added.`, 'success');
            setOpen(false);
            await refresh();
        } catch (error) {
            notify(`Could not create the reference: ${(error as Error).message}`, 'error');
        } finally {
            setBusy(false);
            setLoading(false);
        }
    };

    const set = (field: keyof WizardFields, value: string) => setFields((prev) => (prev ? { ...prev, [field]: value } : prev));

    return (
        <Box paddingTop={2}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ marginBottom: 2 }}>
                <Verified /><Typography variant="h6">Reference</Typography>
            </Stack>
            {reference ? (
                <Alert severity="success" action={
                    <Button color="inherit" size="small" endIcon={<ArrowForward />} onClick={() => navigate(`/references/${encodeURIComponent(reference.projectNo)}`)}>
                        Open reference
                    </Button>
                }>
                    {reference.linked
                        ? `This project is reference ${reference.projectNo}.`
                        : `Reference ${reference.projectNo} carries the same number as this project.`}
                </Alert>
            ) : !REFERENCE_CONVERSION_ENABLED ? (
                <Alert severity="info">
                    No reference with number {project.project_number || '-'} yet. References are currently created manually in
                    the References view - once a reference with the same number exists, it shows up here.
                </Alert>
            ) : (
                <>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        When a project has been closed successfully it becomes a reference. The reference takes over the title, client,
                        countries, dates, contract value, partners and the expert team of this project; you complete the descriptive texts.
                        The project is then set to Closed / Successful.
                    </Typography>
                    {project.status !== 'Closed' && (
                        <Alert severity="info" sx={{ marginBottom: 2 }}>The project is still "{project.status || 'without status'}" - usually you convert it once it is closed.</Alert>
                    )}
                    {project.closure_outcome === 'Terminated' && (
                        <Alert severity="warning" sx={{ marginBottom: 2 }}>This project was terminated - check whether it should really become a reference.</Alert>
                    )}
                    <Button variant="contained" startIcon={<Verified />} onClick={openWizard}>Close &amp; create reference</Button>
                </>
            )}

            <Dialog open={open} onClose={() => !busy && setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Create reference from project {project.project_number}</DialogTitle>
                <DialogContent>
                    {!draft || !fields ? (
                        <Box display="flex" justifyContent="center" paddingY={6}><CircularProgress /></Box>
                    ) : (
                        <>
                            {draft.referenceExists && (
                                <Alert severity="info" sx={{ marginBottom: 2 }}>
                                    A reference with number {draft.ProjectNo} already exists. It will be linked to this project and missing experts/partners
                                    are added to it; its texts are not changed.
                                </Alert>
                            )}
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Taken over: client {draft.short || '-'} · {draft.Country || 'no countries'} · value{' '}
                                {formatMoney(draft.ProjectValueEuro ?? draft.ProjectValueDollar, draft.ProjectValueDollar ? 'USD' : 'EUR')} ·{' '}
                                {draft.Lead ? 'INT lead' : 'INT partner'} · {draft.expertCount} expert(s) · {draft.partnerCount} partner(s)
                                {draft.AssociatedFirms ? ` (${draft.AssociatedFirms})` : ''}
                            </Typography>
                            <Grid container spacing={2} sx={{ marginTop: 0.5 }}>
                                <Grid item xs={12}>
                                    <TextField fullWidth required multiline label="Reference title" value={fields.ProjectTitle}
                                        onChange={(e) => set('ProjectTitle', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Short title" value={fields.ProjectTitle_short}
                                        onChange={(e) => set('ProjectTitle_short', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Location" value={fields.Location} onChange={(e) => set('Location', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth type="date" label="Start date" InputLabelProps={{ shrink: true }} value={fields.StartDate}
                                        onChange={(e) => set('StartDate', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth type="date" label="Completion date" InputLabelProps={{ shrink: true }} value={fields.CompletionDate}
                                        onChange={(e) => set('CompletionDate', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Field of service" value={fields.FieldOfService}
                                        helperText="Separate several with ; " onChange={(e) => set('FieldOfService', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline minRows={3} label="Short description" value={fields.ProjectDescription_short}
                                        onChange={(e) => set('ProjectDescription_short', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline minRows={3} label="Services provided" value={fields.ServicesProvided}
                                        onChange={(e) => set('ServicesProvided', e.target.value)} disabled={draft.referenceExists} />
                                </Grid>
                            </Grid>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 2 }}>
                                All other reference texts (background, objectives, activities, variants) can be completed afterwards in the reference itself.
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button variant="contained" onClick={convert} disabled={busy || !draft}>
                        {draft?.referenceExists ? 'Link reference' : 'Create reference'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ProjectReferenceTab;
